/**
 * AttendanceStatistics.js
 * Main Statistics page displaying group attendance graphs grouped by course
 * All course sections and charts are displayed together without tabs
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Form,
  Button,
  Spinner,
  Alert,
  Badge,
  InputGroup,
  Dropdown,
  Tabs,
  Tab,
} from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { rollCallService, commonService } from '../../services/api';
import GroupAttendanceChart from './GroupAttendanceChart';
import EnrollmentStatisticsTable from './EnrollmentStatisticsTable';
import RevenueStatisticsTab from './RevenueStatisticsTab';
import './AttendanceStatistics.css';

const AttendanceStatistics = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Helper to parse dates from URL or default to last 3 months
  const getDefaultStartDate = () => moment().subtract(90, 'days').startOf('day').toDate();
  const getDefaultEndDate = () => moment().endOf('day').toDate();

  const parseUrlDate = (param, fallback) => {
    if (param && moment(param, 'YYYY-MM-DD', true).isValid()) {
      return moment(param, 'YYYY-MM-DD').toDate();
    }
    return fallback;
  };

  const parseUrlAcademicYear = (param) => {
    if (param && !isNaN(parseInt(param, 10))) {
      return parseInt(param, 10);
    }
    return null;
  };

  const initialStartDate = parseUrlDate(searchParams.get('startDate'), getDefaultStartDate());
  const initialEndDate = parseUrlDate(searchParams.get('endDate'), getDefaultEndDate());
  const initialHideEmpty = searchParams.get('hideEmpty') === 'true';
  const initialSearch = searchParams.get('search') || '';
  const initialAcademicYear = parseUrlAcademicYear(searchParams.get('academicYear'));

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);
  const [hideEmpty, setHideEmpty] = useState(initialHideEmpty);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState(initialAcademicYear);

  // Available academic years for dropdown filter
  const [availableAcademicYears, setAvailableAcademicYears] = useState([]);

  // Statistics data for all courses: List of CourseAttendanceStatistics
  const [coursesStats, setCoursesStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sync state to URL search params
  const updateUrlParams = useCallback(
    (newStartDate, newEndDate, newHideEmpty, newSearch, newAcademicYear) => {
      const params = new URLSearchParams();
      if (newStartDate) params.set('startDate', moment(newStartDate).format('YYYY-MM-DD'));
      if (newEndDate) params.set('endDate', moment(newEndDate).format('YYYY-MM-DD'));
      if (newHideEmpty) params.set('hideEmpty', 'true');
      if (newSearch) params.set('search', newSearch);
      if (newAcademicYear) params.set('academicYear', newAcademicYear.toString());
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  // Load available academic years for the dropdown
  useEffect(() => {
    let isMounted = true;
    const loadAcademicYears = async () => {
      try {
        const res = await commonService.getCurrentAcademicYear();
        const baseYear = (res && res.success && res.data) ? res.data : new Date().getFullYear();
        const yearsSet = new Set();
        // Standard window: baseYear - 3 .. baseYear + 1
        for (let i = -3; i <= 1; i++) {
          yearsSet.add(baseYear + i);
        }
        // Also add any academic years found in the returned courses
        coursesStats.forEach((c) => {
          if (c.academicYear) yearsSet.add(c.academicYear);
        });
        if (selectedAcademicYear) {
          yearsSet.add(selectedAcademicYear);
        }
        const sorted = Array.from(yearsSet).sort((a, b) => b - a);
        if (isMounted) {
          setAvailableAcademicYears(sorted);
        }
      } catch (err) {
        console.error('Error loading academic year options:', err);
      }
    };
    loadAcademicYears();
    return () => {
      isMounted = false;
    };
  }, [coursesStats, selectedAcademicYear]);

  // Load all course statistics for the selected date range and academic year
  const loadStatistics = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const res = await rollCallService.getCourseAttendanceStatistics(
        null,
        startDate,
        endDate,
        selectedAcademicYear
      );
      if (res.success) {
        setCoursesStats(Array.isArray(res.data) ? res.data : []);
      } else {
        setError(res.message || 'Errore nel caricamento delle statistiche');
      }
    } catch (err) {
      console.error('Error loading attendance statistics:', err);
      setError('Errore di connessione al server durante il recupero dei dati');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedAcademicYear]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  // Handle preset date buttons
  const handlePreset = (presetType) => {
    const end = moment().endOf('day').toDate();
    let start;
    if (presetType === '30days') {
      start = moment().subtract(30, 'days').startOf('day').toDate();
    } else if (presetType === '90days') {
      start = moment().subtract(90, 'days').startOf('day').toDate();
    } else if (presetType === '180days') {
      start = moment().subtract(180, 'days').startOf('day').toDate();
    } else if (presetType === 'currentYear') {
      start = moment().startOf('year').toDate();
    }

    setStartDate(start);
    setEndDate(end);
    updateUrlParams(start, end, hideEmpty, searchTerm, selectedAcademicYear);
  };

  const handleHideEmptyToggle = (e) => {
    const checked = e.target.checked;
    setHideEmpty(checked);
    updateUrlParams(startDate, endDate, checked, searchTerm, selectedAcademicYear);
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchTerm(val);
    updateUrlParams(startDate, endDate, hideEmpty, val, selectedAcademicYear);
  };

  const handleAcademicYearChange = (e) => {
    const val = e.target.value ? parseInt(e.target.value, 10) : null;
    setSelectedAcademicYear(val);
    updateUrlParams(startDate, endDate, hideEmpty, searchTerm, val);
  };

  const handlePointClick = (groupId, pointDate) => {
    const dateFormatted = moment(pointDate).format('YYYY-MM-DD');
    navigate(`/groups/${groupId}/rollcall?date=${dateFormatted}`);
  };

  // Filter courses and groups by academicYear, hideEmpty switch and search term
  const processedCourses = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();

    return coursesStats
      .filter((course) => {
        // Se selezionato l'anno sportivo, filtra solo i corsi appartenenti a quell'anno
        if (selectedAcademicYear && course.academicYear !== selectedAcademicYear) {
          return false;
        }
        return true;
      })
      .map((course) => {
        const groups = (course.groups || []).filter((g) => {
          // Hide empty filter
          if (hideEmpty && g.totalRollCalls === 0) return false;

          // Search term filter: check group name or course name
          if (term) {
            const matchCourse = course.courseName.toLowerCase().includes(term);
            const matchGroup = g.groupName.toLowerCase().includes(term);
            return matchCourse || matchGroup;
          }

          return true;
        });

        return {
          ...course,
          visibleGroups: groups,
        };
      })
      .filter((course) => {
        // If searching or hiding empty, only show courses that have matching groups
        if (searchTerm.trim() || hideEmpty) {
          return course.visibleGroups.length > 0;
        }
        return true;
      });
  }, [coursesStats, selectedAcademicYear, hideEmpty, searchTerm]);

  // Overall totals across visible courses
  const totals = useMemo(() => {
    let totalGroups = 0;
    let totalRollCalls = 0;
    processedCourses.forEach((c) => {
      totalGroups += c.visibleGroups.length;
      c.visibleGroups.forEach((g) => {
        totalRollCalls += g.totalRollCalls;
      });
    });
    return {
      coursesCount: processedCourses.length,
      groupsCount: totalGroups,
      rollCallsCount: totalRollCalls,
    };
  }, [processedCourses]);

  const scrollToCourse = (courseId) => {
    const el = document.getElementById(`course-section-${courseId}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="attendance-statistics">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-1 text-primary fw-bold">
            <i className="fas fa-chart-line me-2"></i>
            Statistiche Partecipazione
          </h2>
          <p className="text-muted mb-0">
            Andamento della partecipazione per gruppo e corso basato sugli appelli registrati
          </p>
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <Tabs defaultActiveKey="attendance" className="mb-4 statistics-nav-tabs" id="statistics-main-tabs">
        <Tab
          eventKey="attendance"
          title={
            <span>
              <i className="fas fa-chart-line me-2"></i>
              Frequenze
            </span>
          }
        >
          {/* Filter Card */}
          <Card className="shadow-sm border-0 mb-4">
        <Card.Body className="p-3">
          <Row className="g-3 align-items-center">
            {/* Date Pickers */}
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small fw-semibold text-muted mb-1">Data Inizio</Form.Label>
              <DatePicker
                selected={startDate}
                onChange={(date) => {
                  if (date) {
                    setStartDate(date);
                    updateUrlParams(date, endDate, hideEmpty, searchTerm, selectedAcademicYear);
                  }
                }}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                maxDate={endDate}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm"
              />
            </Col>
            <Col xs={12} sm={6} md={3}>
              <Form.Label className="small fw-semibold text-muted mb-1">Data Fine</Form.Label>
              <DatePicker
                selected={endDate}
                onChange={(date) => {
                  if (date) {
                    setEndDate(date);
                    updateUrlParams(startDate, date, hideEmpty, searchTerm, selectedAcademicYear);
                  }
                }}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                dateFormat="dd/MM/yyyy"
                className="form-control form-control-sm"
              />
            </Col>

            {/* Academic Year Filter */}
            <Col xs={12} sm={6} md={2}>
              <Form.Label className="small fw-semibold text-muted mb-1">Anno Sportivo</Form.Label>
              <Form.Select
                size="sm"
                value={selectedAcademicYear || ''}
                onChange={handleAcademicYearChange}
                aria-label="Filtro Anno Sportivo"
              >
                <option value="">Tutti gli anni</option>
                {availableAcademicYears.map((year) => (
                  <option key={year} value={year}>
                    {year}/{year + 1}
                  </option>
                ))}
              </Form.Select>
            </Col>

            {/* Presets and options */}
            <Col xs={12} md={4}>
              <Form.Label className="small fw-semibold text-muted mb-1 d-block">
                Intervalli Rapidi
              </Form.Label>
              <div className="d-flex flex-wrap gap-2 align-items-center">
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="preset-btn"
                  onClick={() => handlePreset('30days')}
                >
                  30G
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="preset-btn"
                  onClick={() => handlePreset('90days')}
                >
                  3M
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="preset-btn"
                  onClick={() => handlePreset('180days')}
                >
                  6M
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="preset-btn"
                  onClick={() => handlePreset('currentYear')}
                >
                  Anno
                </Button>

                <div className="ms-auto pt-1">
                  <Form.Check
                    type="switch"
                    id="hide-empty-switch"
                    label="Nascondi vuoti"
                    checked={hideEmpty}
                    onChange={handleHideEmptyToggle}
                    className="small fw-semibold"
                  />
                </div>
              </div>
            </Col>
          </Row>

          <hr className="my-3 text-black-50" />

          {/* Search bar and summary counters */}
          <Row className="g-2 align-items-center">
            <Col xs={12} md={6} lg={4}>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white">
                  <i className="fas fa-search text-muted"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Cerca per corso o gruppo..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="border-start-0"
                />
                {searchTerm && (
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => {
                      setSearchTerm('');
                      updateUrlParams(startDate, endDate, hideEmpty, '', selectedAcademicYear);
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </Button>
                )}
              </InputGroup>
            </Col>

            <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end align-items-center gap-2 flex-wrap">
              {selectedAcademicYear && (
                <Badge bg="info" text="dark" className="px-2 py-2">
                  <i className="fas fa-calendar-alt me-1"></i>
                  Anno: {selectedAcademicYear}/{selectedAcademicYear + 1}
                </Badge>
              )}
              <Badge bg="light" text="dark" className="border px-2 py-2">
                <i className="fas fa-graduation-cap me-1 text-primary"></i>
                {totals.coursesCount} {totals.coursesCount === 1 ? 'Corso' : 'Corsi'}
              </Badge>
              <Badge bg="light" text="dark" className="border px-2 py-2">
                <i className="fas fa-users me-1 text-info"></i>
                {totals.groupsCount} {totals.groupsCount === 1 ? 'Gruppo' : 'Gruppi'}
              </Badge>
              <Badge bg="light" text="dark" className="border px-2 py-2">
                <i className="fas fa-clipboard-check me-1 text-success"></i>
                {totals.rollCallsCount} Appelli totali
              </Badge>

              {processedCourses.length > 2 && (
                <Dropdown align="end">
                  <Dropdown.Toggle size="sm" variant="outline-secondary" className="py-1">
                    <i className="fas fa-list me-1"></i> Salta al corso
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ maxHeight: '280px', overflowY: 'auto' }}>
                    <Dropdown.Header>Corsi disponibili:</Dropdown.Header>
                    {processedCourses.map((c) => (
                      <Dropdown.Item
                        key={c.courseId}
                        onClick={() => scrollToCourse(c.courseId)}
                      >
                        {c.courseName} ({c.visibleGroups.length} gruppi)
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabella Iscrizioni */}
      <EnrollmentStatisticsTable academicYear={selectedAcademicYear} />

      {/* Main Content: All Courses & Group Charts together */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Caricamento statistiche di partecipazione in corso...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
          <div className="mt-2">
            <Button variant="outline-danger" size="sm" onClick={loadStatistics}>
              Riprova
            </Button>
          </div>
        </Alert>
      ) : coursesStats.length === 0 ? (
        <Alert variant="info" className="text-center py-4">
          <i className="fas fa-info-circle fa-2x mb-2 d-block"></i>
          Nessun corso o dato trovato nell'intervallo temporale selezionato.
        </Alert>
      ) : processedCourses.length === 0 ? (
        <Alert variant="info" className="text-center py-4">
          <i className="fas fa-info-circle fa-2x mb-2 d-block"></i>
          Nessun gruppo corrisponde ai filtri selezionati.
          {(hideEmpty || searchTerm || selectedAcademicYear) && (
            <div className="mt-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  setHideEmpty(false);
                  setSearchTerm('');
                  setSelectedAcademicYear(null);
                  updateUrlParams(startDate, endDate, false, '', null);
                }}
              >
                Reimposta filtri
              </Button>
            </div>
          )}
        </Alert>
      ) : (
        <div className="courses-statistics-container">
          {processedCourses.map((course) => {
            const courseTotalRollCalls = course.visibleGroups.reduce(
              (acc, g) => acc + g.totalRollCalls,
              0
            );

            return (
              <div
                key={course.courseId}
                id={`course-section-${course.courseId}`}
                className="course-section mb-5"
                data-testid={`course-section-${course.courseId}`}
              >
                {/* Section Header for Course */}
                <div className="course-section-header p-3 mb-3 rounded-3 shadow-sm d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <span className="course-icon-badge">
                      <i className="fas fa-graduation-cap text-primary fs-5"></i>
                    </span>
                    <div>
                      <h4 className="mb-0 fw-bold text-dark">{course.courseName}</h4>
                      <small className="text-muted">
                        Anno Sportivo {course.academicYear}/{course.academicYear + 1}
                      </small>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <Badge bg="light" text="dark" className="border px-2 py-1">
                      {course.visibleGroups.length}{' '}
                      {course.visibleGroups.length === 1 ? 'gruppo' : 'gruppi'}
                    </Badge>
                    <Badge bg="primary" className="px-2 py-1">
                      {courseTotalRollCalls} appelli
                    </Badge>
                  </div>
                </div>

                {/* Group Charts Grid */}
                {course.visibleGroups.length === 0 ? (
                  <Alert variant="light" className="text-muted text-center py-3 border">
                    Nessun gruppo disponibile per questo corso.
                  </Alert>
                ) : (
                  <Row className="g-4">
                    {course.visibleGroups.map((group) => (
                      <Col key={group.groupId} xs={12} xl={6}>
                        <GroupAttendanceChart
                          group={group}
                          onPointClick={handlePointClick}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </div>
            );
          })}
        </div>
      )}
        </Tab>

        <Tab
          eventKey="revenue"
          title={
            <span>
              <i className="fas fa-euro-sign me-2"></i>
              Incassi
            </span>
          }
        >
          <RevenueStatisticsTab />
        </Tab>
      </Tabs>
    </div>
  );
};

export default AttendanceStatistics;
